// The signed-in account, cached so the gate does not depend on the network
// (see App.tsx) and so a screen can ask what this deployment can do without
// fetching it again.

import type { AvailableModel, Me, Tier } from "../api/client";
import { clearAll } from "./db";

const ME_KEY = "metis.me";
// Whose documents and unsent edits IndexedDB holds. Kept apart from ME_KEY,
// which is cleared whenever a session expires: an expired session is the same
// person coming back, and their unsent edits must survive it.
const OWNER_KEY = "metis.cache-owner";
// Both tiers, because the globe is about whether anything here can search and
// only the research call does — which the small tier answers (D35).
const TIERS: Tier[] = ["review", "small"];

export function rememberMe(me: Me | null): void {
  try {
    if (me) localStorage.setItem(ME_KEY, JSON.stringify(me));
    else localStorage.removeItem(ME_KEY);
  } catch {
    // storage can be unavailable; the gate falls back to asking the server
  }
}

export function rememberedMe(): Me | null {
  try {
    const raw = localStorage.getItem(ME_KEY);
    return raw ? (JSON.parse(raw) as Me) : null;
  } catch {
    return null;
  }
}

/** Record that what this device caches from now on belongs to this account. */
export function claimCache(email: string): void {
  try {
    localStorage.setItem(OWNER_KEY, email.toLowerCase());
  } catch {
    // storage can be unavailable
  }
}

/** A different account is signing in on this device: whatever the last one
 *  left here must not be the first thing the new one sees. */
export async function releaseCacheUnlessOwnedBy(email: string): Promise<void> {
  let owner: string | null = null;
  try {
    owner = localStorage.getItem(OWNER_KEY);
  } catch {
    return;
  }
  if (owner !== null && owner !== email.toLowerCase()) await forgetDevice();
}

/** Signing out or deleting the account: nothing of it stays on the device. */
export async function forgetDevice(): Promise<void> {
  await clearAll();
  rememberMe(null);
  try {
    localStorage.removeItem(OWNER_KEY);
  } catch {
    // nothing to clear
  }
}

/** The model a tier actually runs on: the override if there is one, else what
 *  this deployment is set to. */
export function modelInUse(me: Me, tier: Tier): AvailableModel | undefined {
  const id = me.model_overrides[tier] ?? me.model_defaults[tier];
  return me.available_models.find((m) => m.id === id);
}

/** Whether the globe can do anything at all here.
 *
 *  Search is a server-side tool, and the rule it has to satisfy is that a
 *  source the advisor cites is a page the author can open (design §9.4, D8).
 *  A model whose search hides the pages it read cannot meet that, so it is not
 *  given search — and offering the author a toggle that would do nothing is
 *  worse than saying so (D25).
 */
export function searchAvailable(me: Me | null): boolean {
  if (!me) return true; // nothing cached yet; the server is still the authority
  return TIERS.some((tier) => modelInUse(me, tier)?.searches === true);
}

/** Whether the advisor reads in the background at all: the author's setting,
 *  and the monthly ceiling, which suspends that and nothing else (§12.3).
 *
 *  The decision is the server's — it holds the key — and this is only what the
 *  strip needs to explain why nothing is happening. */
export function backgroundAllowed(me: Me | null): boolean {
  return me !== null && me.background_enabled && !me.budget.over;
}
